var m = require('mithril');
var key = require('../../libs/keybindings');
var Model = require('../../libs/Model');
var modal = require('../../layout/components/modal');
var loaders = require('../../layout/components/loaders');
var Point = require('../models/point');
var pointTd = require('./pointTd');
var writeValueControl = require('./writeValueControl');
var canWriteColumn = require('../canWriteColumn');
var writeCommandEvents = require('../writeCommandEvents');
var writeOperations = require('../writeOperation');

var component = {};

component.errors = [];
component.successes = [];
component.rows = [];
component.title = 'Mass Write';
component.parentRid = null;
component.loading = false;
component.saving = false;
component.baselinePointRecord = null;
component.formValue = null;
component.increment = 1;
component.loadToken = 0;
component.saveToken = 0;

function normalizeRid(rid) {
  if (rid && typeof rid === 'object') {
    rid = rid['@rid'];
  }
  if (typeof rid !== 'string' || !rid) {
    return null;
  }
  return rid[0] === '#' ? rid.slice(1) : rid;
}

function ridForUrl(rid) {
  var normalized = normalizeRid(rid);
  return normalized ? normalized : null;
}

function ridWithHash(rid) {
  var normalized = normalizeRid(rid);
  return normalized ? '#' + normalized : null;
}

function stableJson(value) {
  if (!value || typeof value !== 'object') return JSON.stringify(value);
  if (Array.isArray(value)) {
    return '[' + value.map(stableJson).join(',') + ']';
  }
  return '{' + Object.keys(value).sort().map(function (key) {
    return JSON.stringify(key) + ':' + stableJson(value[key]);
  }).join(',') + '}';
}

function normalizeNumber(value, fallback) {
  var numberValue = Number(value);
  return Number.isFinite(numberValue) ? numberValue : fallback;
}

function pointConfigSignature(pointRecord) {
  pointRecord = pointRecord || {};
  return [
    pointRecord.unit || '',
    pointRecord.destUnit || '',
    pointRecord.displayUnit || '',
    normalizeNumber(pointRecord.scale, 1),
    normalizeNumber(pointRecord.offset, 0),
    pointRecord.unit === 'bool' ? '' : stableJson(pointRecord.enumeration)
  ].join('|');
}

function configMismatchReason(pointRecord, baselinePointRecord) {
  if (!pointRecord || !pointRecord['@rid']) {
    return 'Point configuration could not be loaded';
  }
  if (!baselinePointRecord || !baselinePointRecord['@rid']) {
    return 'Baseline point configuration is still loading';
  }
  if (pointConfigSignature(pointRecord) !== pointConfigSignature(baselinePointRecord)) {
    return 'Point configuration differs from the first eligible point in this column';
  }
  return null;
}

function isEnabled(value) {
  return value === true || value === 1 || value === '1';
}

function pointSupportsWrite(pointRecord) {
  return !!(pointRecord && isEnabled(pointRecord.writable));
}

function displayPath(row) {
  if (row && row.path) return row.path;
  var point = row && row.columnRecord && row.columnRecord.in;
  return (point && (point.name || point['@rid'])) || 'Point';
}

function initializeFormValue(pointRecord) {
  if (!pointRecord || !pointRecord['@rid']) {
    component.formValue = null;
    component.increment = 1;
    return;
  }

  if (pointRecord.unit === 'bool') {
    component.formValue = Boolean(Number(pointRecord.value));
    component.increment = 1;
  } else if (pointRecord.enumeration) {
    component.formValue = pointRecord.value;
    component.increment = 1;
  } else {
    component.formValue = Point.readValue({
      value: parseFloat(pointRecord.value),
      unit: pointRecord.unit,
      destUnit: pointRecord.destUnit,
      displayUnit: pointRecord.displayUnit,
      scale: pointRecord.scale,
      offset: pointRecord.offset
    });
    component.increment = Number(pointRecord.increment) || 1;
  }
}

function writeValueForPoint(pointRecord, formValue) {
  if (arguments.length < 2) {
    formValue = component.formValue;
  }
  if (formValue === undefined || formValue === null || formValue === '') {
    return null;
  }

  if (pointRecord && pointRecord.unit === 'bool') {
    return Number(Boolean(Number(formValue)));
  }

  return Point.writeValue({
    scale: pointRecord && pointRecord.scale,
    unit: pointRecord && pointRecord.unit,
    destUnit: pointRecord && pointRecord.destUnit,
    displayUnit: pointRecord && pointRecord.displayUnit,
    offset: pointRecord && pointRecord.offset
  }, formValue);
}

function displayWriteValue(row) {
  var pointRecord = row && row.pointRecord || component.baselinePointRecord;
  if (!component.baselinePointRecord || component.formValue === null || typeof component.formValue === 'undefined') {
    return 'Loading';
  }
  if (pointRecord && pointRecord.unit === 'bool') {
    return component.formValue ? 'On' : 'Off';
  }
  if (pointRecord && pointRecord.enumeration && pointRecord.unit !== 'bool') {
    return pointRecord.enumeration[component.formValue] || component.formValue;
  }
  var unit = Point.formatUnit(pointRecord || {});
  return String(component.formValue) + (unit ? ' ' + unit : '');
}

function redrawLoadingState() {
  if (m.redraw && typeof m.redraw.sync === 'function') {
    m.redraw.sync();
  } else {
    m.redraw();
  }
}

function waitForPaint() {
  return new Promise(function (resolve) {
    if (typeof window !== 'undefined' && typeof window.requestAnimationFrame === 'function') {
      window.requestAnimationFrame(function () { resolve(); });
    } else {
      setTimeout(resolve, 0);
    }
  });
}

function intrinsicDisabledReason(row) {
  if (row.loadError) return row.loadError;
  if (!row.sourcePointRecord || !row.sourcePointRecord['@rid']) {
    return 'Source point configuration could not be loaded';
  }
  if (!row.pointRecord || !row.pointRecord['@rid']) {
    return 'Point configuration could not be loaded';
  }
  if (!pointSupportsWrite(row.sourcePointRecord)) {
    return row.pointRecord !== row.sourcePointRecord
      ? 'Source point is not configured for writes'
      : 'Point is not configured for writes';
  }
  if (!pointSupportsWrite(row.pointRecord)) {
    return row.pointRecord !== row.sourcePointRecord
      ? 'Write target is not configured for writes'
      : 'Point is not configured for writes';
  }
  if (!canWriteColumn(row.columnRecord)) {
    return 'You do not have write permission for this point';
  }
  return null;
}

function rowCanBeBaseline(row) {
  return !intrinsicDisabledReason(row);
}

function updateRowEligibility(row) {
  var disabledReason = intrinsicDisabledReason(row);
  if (!disabledReason) {
    disabledReason = configMismatchReason(row.pointRecord, component.baselinePointRecord);
  }
  row.disabledReason = disabledReason;
  row.disabled = !!disabledReason;
  row.selected = !row.disabled;
}

function loadPointRecord(row) {
  var sourcePointRid = ridForUrl(row && row.columnRecord && row.columnRecord.in && row.columnRecord.in['@rid']);
  if (!sourcePointRid) {
    row.disabled = true;
    row.loadError = 'Point RID is missing';
    row.disabledReason = row.loadError;
    return Promise.resolve(row);
  }

  return loaders.requestWithLoader({
    loader: 'background',
    url: '/api/v1/point/' + sourcePointRid,
    method: 'GET'
  })
  .then(function (sourcePointRecord) {
    row.sourcePointRecord = sourcePointRecord;
    var alternateRid = ridForUrl(sourcePointRecord && sourcePointRecord.writeAlternatePoint) ||
      ridForUrl(row.columnRecord && row.columnRecord.in && row.columnRecord.in.writeAlternatePoint);
    if (alternateRid && alternateRid !== sourcePointRid) {
      return loaders.requestWithLoader({
        loader: 'background',
        url: '/api/v1/point/' + alternateRid,
        method: 'GET'
      })
      .then(function (alternatePointRecord) {
        row.pointRecord = alternatePointRecord;
        row.writePointRid = ridWithHash(alternatePointRecord && alternatePointRecord['@rid']);
        return row;
      });
    }

    row.pointRecord = sourcePointRecord;
    row.writePointRid = ridWithHash(sourcePointRecord && sourcePointRecord['@rid']);
    return row;
  })
  .catch(function (error) {
    row.disabled = true;
    row.loadError = (error && (error.error || error.message)) || 'Point configuration could not be loaded';
    row.disabledReason = row.loadError;
    row.status = 'error';
    row.statusMessage = row.disabledReason;
    return row;
  });
}

function loadRows() {
  var token = ++component.loadToken;
  component.loading = true;
  return Promise.all(component.rows.map(loadPointRecord))
  .then(function () {
    if (token !== component.loadToken) return;
    component.baselinePointRecord = component.rows
      .filter(rowCanBeBaseline)
      .map(function (row) { return row.pointRecord; })[0] || null;
    initializeFormValue(component.baselinePointRecord);
    component.rows.forEach(updateRowEligibility);
    component.loading = false;
    m.redraw();
  })
  .catch(function (error) {
    if (token !== component.loadToken) return;
    component.loading = false;
    component.errors = [(error && (error.error || error.message)) || 'Unable to load mass write points'];
    m.redraw();
  });
}

function enabledRows() {
  return component.rows.filter(function (row) {
    return !row.disabled;
  });
}

function selectedRows() {
  return enabledRows().filter(function (row) {
    return row.selected;
  });
}

function allEnabledRowsSelected() {
  var rows = enabledRows();
  return rows.length > 0 && rows.every(function (row) {
    return !!row.selected;
  });
}

function someEnabledRowsSelected() {
  return component.rows.filter(function (row) {
    return row.selected && !row.disabled;
  }).length > 0;
}

function toggleAllRows() {
  var checked = !allEnabledRowsSelected();
  enabledRows().forEach(function (row) {
    row.selected = checked;
  });
}

function selectionButton(options) {
  var checked = !!options.checked;
  var mixed = !!options.mixed;
  var disabled = !!options.disabled;
  var className = 'ba bw1 br1 flex items-center justify-center center';
  if (options.class) className += ' ' + options.class;

  return m('button', {
    type: 'button',
    class: className,
    style: {
      width: '1.35rem',
      height: '1.35rem',
      minWidth: '1.35rem',
      padding: 0,
      borderColor: checked || mixed ? '#58abe2' : '#777',
      backgroundColor: checked || mixed ? '#58abe2' : 'transparent',
      color: checked || mixed ? '#111' : '#f4f4f4',
      cursor: disabled ? 'not-allowed' : 'pointer',
      opacity: disabled ? 0.5 : 1,
      lineHeight: 1
    },
    disabled: disabled,
    title: options.label,
    'aria-label': options.label,
    'aria-checked': mixed ? 'mixed' : checked ? 'true' : 'false',
    role: 'checkbox',
    'data-select-all-children': options.selectAll ? true : null,
    onclick: function (evt) {
      evt.preventDefault();
      evt.stopPropagation();
      if (!disabled && typeof options.onclick === 'function') {
        options.onclick();
      }
    },
    onkeydown: function (evt) {
      if (evt.key !== 'Enter' && evt.key !== ' ') return;
      evt.preventDefault();
      evt.stopPropagation();
      if (!disabled && typeof options.onclick === 'function') {
        options.onclick();
      }
    }
  }, [
    checked ? m('i.f4.ion-checkmark') : mixed ? m('span.b.f4', '-') : null
  ]);
}

function selectAllCheckbox() {
  var enabledCount = enabledRows().length;
  var checked = allEnabledRowsSelected();
  var disabled = component.loading || component.saving || enabledCount === 0;
  var label = enabledCount === 0
    ? 'No writable points to select'
    : checked ? 'Deselect all writable points' : 'Select all writable points';

  return selectionButton({
    checked: checked,
    mixed: !checked && someEnabledRowsSelected(),
    disabled: disabled,
    label: label,
    class: 'mass-write-select-all-checkbox',
    selectAll: true,
    onclick: toggleAllRows
  });
}

function rowCheckbox(row) {
  var checked = !!row.selected;
  var disabled = !!row.disabled || component.saving;
  var label = row.disabledReason || (checked ? 'Exclude point from write' : 'Include point in write');
  return selectionButton({
    checked: checked,
    disabled: disabled,
    label: label,
    class: 'mass-write-row-checkbox',
    onclick: function () {
      row.selected = !row.selected;
    }
  });
}

function statusCell(row) {
  if (row.status === 'loading') {
    return m('span.inline-flex.items-center.justify-center', [
      m('i.f3.blue.ion-load-a.spin', { title: 'Writing' })
    ]);
  }
  if (row.status === 'success') {
    return m('i.f3.green.ion-checkmark', { title: 'Write succeeded' });
  }
  if (row.status === 'warning') {
    return m('i.f3.gold.ion-alert-circled', {
      title: row.statusMessage || 'Write was terminal without a new request'
    });
  }
  if (row.status === 'error') {
    return m('i.f3.red.ion-close-round', { title: row.statusMessage || 'Write failed' });
  }
  if (row.disabledReason) {
    return m('span.f6.moon-gray', { title: row.disabledReason }, 'Disabled');
  }
  return '';
}

function validNumericFormValue(pointRecord, formValue) {
  if (pointRecord && (pointRecord.unit === 'bool' || pointRecord.enumeration)) {
    return true;
  }
  if (formValue === undefined || formValue === null) return false;
  if (typeof formValue === 'string' && formValue.trim() === '') return false;
  return Number.isFinite(Number(formValue));
}

function operationIsCurrent(token) {
  return token === component.saveToken;
}

component.editDialogue = function (options) {
  options = options || {};
  component.saveToken += 1;
  component.errors = [];
  component.successes = [];
  component.parentRid = normalizeRid(options.parentRid);
  component.title = options.title || 'Mass Write';
  component.rows = (options.rows || []).map(function (row, index) {
    return Object.assign({
      index: index,
      selected: false,
      disabled: true,
      disabledReason: 'Point configuration is loading',
      status: null,
      statusMessage: null
    }, row);
  });
  component.loading = true;
  component.saving = false;
  component.baselinePointRecord = null;
  component.formValue = null;
  component.increment = 1;
  component.writeOperations = {};

  var submit = function () {
    return component.save();
  };

  key.setScope('modal');
  key('enter', 'modal', submit);

  modal.vm.title(component.title);
  modal.vm.body = component.view;
  modal.vm.buttons([
    {
      label: 'Send',
      class: 'btn-good',
      disabled: function () {
        return component.loading || component.saving || selectedRows().length === 0;
      },
      func: submit
    }
  ]);
  modal.vm.open({
    extrawidth: true,
    suppressPointUpdates: component.rows.map(function (row) {
      return row && row.columnRecord && row.columnRecord.in && row.columnRecord.in['@rid'];
    })
  });

  return loadRows();
};

component.save = async function () {
  if (component.loading || component.saving) return false;
  if (!component.parentRid) {
    component.errors = ['Cannot write points without a parent location or equipment context.'];
    m.redraw();
    return false;
  }

  var rowsToWrite = selectedRows();
  if (rowsToWrite.length === 0) {
    component.errors = ['Select at least one point to write.'];
    m.redraw();
    return false;
  }

  var parentRid = component.parentRid;
  var formValue = component.formValue;
  var invalidValue = rowsToWrite.some(function (row) {
    return !validNumericFormValue(row.pointRecord, formValue);
  });
  if (invalidValue) {
    component.errors = ['Enter a finite numeric value before sending writes.'];
    m.redraw();
    return false;
  }

  var missingWriteConfiguration = rowsToWrite.find(function (row) {
    return !Point.writeConfigFingerprint(row.sourcePointRecord) ||
      !Point.writeConfigFingerprint(row.pointRecord);
  });
  if (missingWriteConfiguration) {
    component.errors = [displayPath(missingWriteConfiguration) +
      ': Point write configuration is unavailable. Reload and try again.'];
    m.redraw();
    return false;
  }

  var writes = rowsToWrite.map(function (row) {
    var sourcePointRid = ridWithHash(
      row.sourcePointRecord && row.sourcePointRecord['@rid'] ||
      row.columnRecord && row.columnRecord.in && row.columnRecord.in['@rid']
    );
    var preparedWrite = {
      row: row,
      // The batch row follows the user's logical source selection. Physical
      // write targets may legitimately move when configuration is refreshed.
      rowKey: sourcePointRid,
      value: writeValueForPoint(row.pointRecord, formValue),
      expectedPointConfiguration: Point.expectedPointConfiguration([
        row.sourcePointRecord,
        row.pointRecord
      ]),
      sourcePointRid: sourcePointRid,
      targetPointRid: ridWithHash(row.writePointRid)
    };
    preparedWrite.body = {
      value: preparedWrite.value,
      commandType: 'write',
      sourcePointRid: preparedWrite.sourcePointRid,
      expectedPointConfiguration: preparedWrite.expectedPointConfiguration
    };
    preparedWrite.operationKey = 'row:' + preparedWrite.rowKey;
    preparedWrite.url = '/api/v1/point/' + ridForUrl(row.writePointRid) +
      '/write/' + parentRid;
    return preparedWrite;
  });
  var durableBatchIntent = {
    parentRid: parentRid,
    formValue: formValue,
    selectedSourcePointRids: writes.map(function (write) {
      return write.rowKey;
    }).sort()
  };
  var actionIntent = stableJson(durableBatchIntent);
  var rowConflictScopes = {};
  var rowConflictGroups = {};
  var rowRequestIdentities = {};
  writes.forEach(function (write) {
    write.intent = actionIntent;
    rowConflictScopes[write.rowKey] = [write.rowKey, write.targetPointRid]
      .filter(function (rid, index, all) { return all.indexOf(rid) === index; })
      .map(function (rid) { return writeOperations.pointConflictScope(rid); });
    rowConflictGroups[write.rowKey] = stableJson({
      surface: 'mass-write',
      batchIntent: durableBatchIntent
    });
    rowRequestIdentities[write.rowKey] = {
      targetPointRid: write.targetPointRid,
      value: write.value,
      expectedPointConfiguration: write.expectedPointConfiguration
    };
  });
  component.writeOperations = component.writeOperations || {};
  var operationToken = ++component.saveToken;
  component.errors = [];
  component.successes = [];
  component.saving = true;
  writes.forEach(function (write) {
    write.row.status = null;
    write.row.statusMessage = null;
  });
  m.redraw();

  var batch;
  var seededOperations;
  try {
    batch = await writeOperations.prepareBatch(
      component.writeOperations,
      'mass-write-batch',
      {
        kind: 'mass-write',
        contextKey: parentRid,
        rows: writes.map(function (write) { return write.rowKey; }),
        intent: durableBatchIntent,
        rowConflictScopes: rowConflictScopes,
        rowConflictGroups: rowConflictGroups,
        rowRequestIdentities: rowRequestIdentities,
        recoveryPolicy: writeOperations.ATTENDED_RECOVERY_POLICY
      }
    );
    writes.forEach(function (write) {
      var durableRow = batch.rows[write.rowKey];
      if (durableRow && durableRow.status !== 'pending') {
        write.row.status = durableRow.status === 'success' ? 'success' : 'warning';
        write.row.statusMessage = durableRow.warning;
        write.row.successIntent = write.intent;
      }
    });
    var pendingWrites = writes.filter(function (write) {
      return !(batch.rows[write.rowKey] && batch.rows[write.rowKey].status !== 'pending');
    });
    seededOperations = await writeOperations.seedBatchOperations(
      batch,
      pendingWrites.map(function (write) {
        return {
          key: write.operationKey,
          rowKey: write.rowKey,
          phase: 'write',
          url: write.url,
          body: write.body,
          scope: [parentRid, write.operationKey].join(':'),
          intent: write.body,
          rowRequestIdentity: rowRequestIdentities[write.rowKey]
        };
      })
    );
    var operationsByRow = {};
    seededOperations.forEach(function (operation) {
      operationsByRow[operation.rowKey] = operation;
    });
    writes = pendingWrites;
    writes.forEach(function (write) {
      write.writeOperation = operationsByRow[write.rowKey];
    });
  } catch (recoveryError) {
    if (!operationIsCurrent(operationToken)) return false;
    component.errors = [
      recoveryError && (recoveryError.error || recoveryError.message) ||
      'Secure mass-write recovery is unavailable.'
    ];
    component.successes = [];
    component.saving = false;
    m.redraw();
    return false;
  }

  if (!operationIsCurrent(operationToken)) return false;
  m.redraw();

  var successCount = 0;
  var failureCount = 0;
  var warningCount = 0;

  for (var idx = 0; idx < writes.length; idx += 1) {
    if (!operationIsCurrent(operationToken)) return false;
    var write = writes[idx];
    var row = write.row;
    row.status = 'loading';
    row.statusMessage = null;
    redrawLoadingState();
    await waitForPaint();
    if (!operationIsCurrent(operationToken)) return false;

    var writeOperation = write.writeOperation;
    var operationKey = write.operationKey;
    try {
      await writeCommandEvents.waitForPointWrite({
        pointRid: writeOperations.pointRidForOperation(writeOperation),
        request: async function () {
          if (!operationIsCurrent(operationToken)) {
            return Promise.reject(new Error('Mass write operation was cancelled.'));
          }
          await writeOperations.markAttempted(writeOperation);
          return loaders.requestWithLoader({
            loader: 'background',
            url: writeOperation.url,
            method: 'PUT',
            body: writeOperation.body
          });
        },
        timeoutMessage: 'Timed out waiting for write confirmation.'
      });
      if (!operationIsCurrent(operationToken)) return false;
      await writeOperations.markBatchRowSuccess(
        component.writeOperations,
        'mass-write-batch',
        batch,
        write.rowKey
      );
      row.status = 'success';
      row.successIntent = write.intent;
      successCount += 1;
    } catch (error) {
      if (!operationIsCurrent(operationToken)) return false;
      if (error && error.writeApplied) {
        row.status = 'success';
        row.successIntent = write.intent;
        successCount += 1;
      } else if (error && error.writeExpired) {
        row.status = 'warning';
        row.statusMessage = error.message;
        warningCount += 1;
      } else {
        if (writeOperation) {
          try {
            await writeOperations.fail(
              component.writeOperations,
              operationKey,
              writeOperation,
              error
            );
          } catch (recoveryError) {
            error = recoveryError;
          }
        }
        row.status = 'error';
        row.statusMessage = (error && (error.error || error.message)) || 'Write failed';
        failureCount += 1;
      }
    }
    m.redraw();
  }

  if (!operationIsCurrent(operationToken)) return false;
  component.saving = false;
  if (successCount) {
    component.successes = ['Successfully sent ' + successCount + ' write' + (successCount === 1 ? '' : 's') + '.'];
  }
  if (failureCount) {
    component.errors = [failureCount + ' write' + (failureCount === 1 ? '' : 's') + ' failed.'];
  }
  if (warningCount) {
    component.errors.push(warningCount + ' write' + (warningCount === 1 ? '' : 's') +
      ' ended without a new request.');
  }
  m.redraw();
};

component.view = function () {
  return m('div.mass-point-write-modal', [
    Model.errorView.call(component),
    Model.successView.call(component),
    component.baselinePointRecord
      ? m('div.mb3', [
        m(writeValueControl, {
          pointRecord: component.baselinePointRecord,
          value: component.formValue,
          increment: component.increment,
          onchange: function (value) {
            component.formValue = value;
          }
        })
      ])
      : m('div.tc.pa2', 'Loading command form...'),
    component.loading
      ? m('div.f5.moon-gray.mb2', 'Loading point configurations...')
      : null,
    m('table.w-100.f4.fw2.collapse', [
      m('thead', [
        m('tr', [
          m('th.bb.bw1.tc.v-btm.ph2', selectAllCheckbox()),
          m('th.bb.bw1.tl.v-btm.ph2', 'Point Path'),
          m('th.bb.bw1.tc.v-btm.ph2', 'Live Value'),
          m('th.bb.bw1.tc.v-btm.ph2', 'Write Value'),
          m('th.bb.bw1.tc.v-btm.ph2', 'Status')
        ])
      ]),
      m('tbody', component.rows.map(function (row) {
        var rowClass = row.disabled ? '.o-60' : '';
        var liveColumnRecord = row.columnRecord || {};
        return m('tr' + rowClass, { key: row.index }, [
          m('td.bb.tc.v-mid.ph2', rowCheckbox(row)),
          m('td.bb.tl.v-mid.ph2', {
            title: row.disabledReason || displayPath(row)
          }, [
            m('div.f5', displayPath(row)),
            row.disabledReason ? m('div.f6.moon-gray.mt1', row.disabledReason) : null
          ]),
          m(pointTd, {
            columnRecord: liveColumnRecord,
            parentRid: component.parentRid,
            options: {
              disableInteractions: true
            },
            tdClass: '.bb.bl-0',
            tdStyle: {
              cursor: 'default',
              pointerEvents: 'none'
            }
          }),
          m('td.bb.tc.v-mid.ph2.nowrap', displayWriteValue(row)),
          m('td.bb.tc.v-mid.ph2', statusCell(row))
        ]);
      }))
    ])
  ]);
};

component._test = {
  pointConfigSignature: pointConfigSignature,
  pointSupportsWrite: pointSupportsWrite,
  intrinsicDisabledReason: intrinsicDisabledReason,
  enabledRows: enabledRows,
  selectedRows: selectedRows,
  allEnabledRowsSelected: allEnabledRowsSelected,
  toggleAllRows: toggleAllRows,
  loadRows: loadRows,
  writeValueForPoint: writeValueForPoint
};

module.exports = component;
